import {
  CartesianGrid,
  Tooltip,
  XAxis,
  AreaChart,
  Area,
  YAxis,
} from "recharts";
import { useMemo, useState } from "react";
import {
  createListCollection,
  Select,
  Portal,
  HStack,
  VStack,
  Text,
  Box,
} from "@chakra-ui/react";
import ChakraDatePicker from "../input/ChakraDatePicker/ChakraDatePicker";
import { useLanguage } from "@/contexts/languageContext";

const getUniqueLogs = (logs) => {
  const seen = new Set();
  return logs.filter((log) => {
    const day = log.createdAt.split("T")[0];
    const key = `${log.userId}_${day}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const generateDateArray = (range, baseDate) => {
  const date = new Date(baseDate);
  const year = date.getFullYear();
  const month = date.getMonth();

  if (range === "d") return [date];

  if (range === "w") {
    const dayOfWeek = date.getDay();
    const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(date);
      d.setDate(diff + i);
      return d;
    });
  }

  if (range === "m") {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
  }

  if (range === "y") {
    const yearArray = [];
    for (let m = 0; m < 12; m++) {
      const daysInMonth = new Date(year, m + 1, 0).getDate();
      for (let d = 1; d <= daysInMonth; d++) {
        yearArray.push(new Date(year, m, d));
      }
    }
    return yearArray;
  }
  return [];
};

const processLogs = (rawData, range, dateArray) => {
  if (range === "d") {
    const dateStr = dateArray[0].toISOString().split("T")[0];
    const todayLogs = rawData.filter((log) => log.createdAt.startsWith(dateStr));
    const hoursMap = Array.from({ length: 24 }, (_, i) => ({ hour: i, number: 0 }));

    todayLogs.forEach((log) => {
      const hour = new Date(log.createdAt).getHours();
      if (hoursMap[hour]) hoursMap[hour].number += 1;
    });

    return hoursMap.map((h) => {
      const d = new Date();
      d.setHours(h.hour, 0, 0, 0);
      return { date: d, number: h.number };
    });
  }

  return dateArray.map((d) => {
    const dateStr = d.toISOString().split("T")[0];
    return {
      date: dateStr,
      number: rawData.filter((log) => log.createdAt.startsWith(dateStr)).length,
    };
  });
};

const calculateTotal = (data) => data.reduce((acc, curr) => acc + curr.number, 0);

const Graph = ({ logs, ...props }) => {
  const { t, language } = useLanguage();
  const [range, setRange] = useState("d");
  const [date, setDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState(1);

  const isUnique = activeTab === 1;

  const uniqueLogs = useMemo(() => getUniqueLogs(logs), [logs]);
  const dateArray = useMemo(() => generateDateArray(range, date), [range, date]);

  const [data, uniqueData, countData, countUniqueData] = useMemo(() => {
    const processedData = processLogs(logs, range, dateArray);
    const processedUniqueData = processLogs(uniqueLogs, range, dateArray);
    return [
      processedData,
      processedUniqueData,
      calculateTotal(processedData),
      calculateTotal(processedUniqueData),
    ];
  }, [logs, uniqueLogs, range, dateArray]);

  const filteredData = isUnique ? uniqueData : data;

  const ranges = createListCollection({
    items: [
      { label: t("stats.ranges.d"), value: "d" },
      { label: t("stats.ranges.w"), value: "w" },
      { label: t("stats.ranges.m"), value: "m" },
      { label: t("stats.ranges.y"), value: "y" },
    ],
  });

  const tabs = [
    { id: 1, dataLength: countUniqueData, dataName: t("stats.uniqueVisits") },
    { id: 2, dataLength: countData, dataName: t("stats.visits") },
  ];

  const TabButton = ({ el }) => {
    const isActive = activeTab === el.id;
    return (
      <Box
        p="4"
        w="16rem"
        cursor="pointer"
        backgroundColor={isActive ? "gray.100" : "transparent"}
        borderTopRadius="xl"
        boxShadow={isActive ? "inset 0px -4px 0px 0px black" : "none"}
        onClick={() => setActiveTab(el.id)}
      >
        <Text color="gray.500" textAlign="left" w="100%">
          {el.dataName}
        </Text>
        <Text fontWeight="bold" fontSize="2xl">
          {el.dataLength}
        </Text>
      </Box>
    );
  };

  return (
    <Box bg="white" borderRadius="4xl" p={5} shadow="sm" border="1px solid" borderColor="gray.100">
      <HStack justifyContent="space-between">
        <HStack gap="0">
          {tabs.map((tab) => (
            <TabButton el={tab} key={tab.id} />
          ))}
        </HStack>
        <VStack className="w-full justify-around">
          <ChakraDatePicker
            selected={date}
            onChange={setDate}
            dateFormat="dd/MM/yyyy"
            range={range}
          />
          <VStack w="100%">
            <Select.Root
              collection={ranges}
              onValueChange={(e) => setRange(e.value[0])}
              value={[range]}
            >
              <Select.HiddenSelect />
              <Select.Control className="w-32">
                <Select.Trigger>
                  <Select.ValueText placeholder="Select range" />
                </Select.Trigger>
                <Select.IndicatorGroup>
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    {ranges.items.map((r) => (
                      <Select.Item item={r} key={r.value}>
                        {r.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
          </VStack>
        </VStack>
      </HStack>

      <AreaChart
        style={{ width: "100%", aspectRatio: 1.618 }}
        responsive
        data={filteredData}
        margin={{ top: 20, right: 20, bottom: 5, left: 0 }}
      >
        <defs>
          <linearGradient id="colorNumber" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#eee" strokeDasharray="3 3" vertical={false} />
        <Area
          type="monotone"
          dataKey="number"
          stroke="#8884d8"
          fill="url(#colorNumber)"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 6 }}
        />
        <XAxis
          dataKey="date"
          minTickGap={50}
          tickFormatter={(str) => {
            const d = new Date(str);
            if (range === "d") return `${d.getHours()} h`;
            if (range === "y") return d.toLocaleDateString(language, { month: "short", year: "numeric" });
            return d.toLocaleDateString(language, { day: "2-digit", month: "short" });
          }}
        />
        <YAxis dataKey="number" />
        <Tooltip
          labelFormatter={(str) =>
            new Date(str).toLocaleDateString(language, {
              day: range === "y" ? undefined : "2-digit",
              month: "long",
              year: "numeric",
            })
          }
        />
      </AreaChart>
    </Box>
  );
};

export default Graph;
